/**
 * MSAL (Microsoft Authentication Library) configuration for Entra ID.
 *
 * Required environment variables (set in .env.production / Azure App Service):
 *   REACT_APP_ENTRA_TENANT_ID   — Azure AD tenant ID (GUID)
 *   REACT_APP_ENTRA_CLIENT_ID   — App Registration client ID (GUID)
 *   REACT_APP_REDIRECT_URI      — OAuth2 redirect URI (optional, defaults to origin)
 *
 * When these vars are absent (local dev, Azure dev) OIDC is disabled and
 * the "Sign in with Microsoft" button is hidden.
 */

import { PublicClientApplication } from '@azure/msal-browser';

const TENANT_ID  = process.env.REACT_APP_ENTRA_TENANT_ID;
const CLIENT_ID  = process.env.REACT_APP_ENTRA_CLIENT_ID;
const REDIRECT   = process.env.REACT_APP_REDIRECT_URI || window.location.origin;

/** Returns true only when all required Entra env vars are configured. */
export const isEntraConfigured = () => !!(TENANT_ID && CLIENT_ID);

export const msalConfig = {
  auth: {
    clientId:    CLIENT_ID  || 'not-configured',
    authority:   `https://login.microsoftonline.com/${TENANT_ID || 'common'}`,
    redirectUri: REDIRECT,
  },
  cache: {
    // sessionStorage keeps tokens in tab scope; cleared when tab closes.
    // More secure than localStorage for browser-based SPAs.
    cacheLocation:          'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

/**
 * Scopes to request when signing in.
 * 'access_as_user' is the custom API scope exposed by the App Registration.
 * 'openid' and 'profile' are added automatically by MSAL.
 */
export const loginRequest = {
  scopes: [`api://${CLIENT_ID}/access_as_user`],
};

/**
 * Singleton MSAL instance.
 * Initialised lazily to avoid errors when REACT_APP_ENTRA_CLIENT_ID is unset.
 */
let _msalInstance = null;

export function getMsalInstance() {
  if (!_msalInstance) {
    _msalInstance = new PublicClientApplication(msalConfig);
  }
  return _msalInstance;
}
